import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type { InsertInternalAuditImpactRatingMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

export type InsertImpactInternalAuditRatingInput = {
  InternalAuditReportId: string;
  RatedItemId: string;
  Ratings: { ImpactId: string; Rating: number }[];
  TestDate: string;
  CompletedBy?: string | null;
  Likelihood?: number | null;
  CustomAttributeData?: CustomAttributeData | null;
};

const mapTrpcResponseToGraphQL = (trpcData: {
  Ids: string[];
}): InsertInternalAuditImpactRatingMutation => ({
  insertChildImpactInternalAuditRating: {
    __typename: 'insertChildImpactRatingOutput',
    Ids: trpcData.Ids,
  },
});

export const useInsertImpactInternalAuditRatingTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.internalAuditResult.insertImpactRating.mutationOptions({
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.internalAuditResult.pathKey(),
          }),
          queryClient.invalidateQueries({
            queryKey:
              trpc.frontend.assessment.internalAuditResultCount.queryKey(),
          }),
        ]);
      },
    }),
  });

  return {
    insertImpactInternalAuditRating: async (
      variables: InsertImpactInternalAuditRatingInput
    ): Promise<InsertInternalAuditImpactRatingMutation> => {
      const result = await mutation.mutateAsync(variables);

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
